import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Kicker, Big, Lines, Disclaimer } from "./ui";
import { SKETCH, InkFilter } from "./Sketches";
import { TESTIMONIALS } from "./content";

const STEPS = [
  ["01", "Decide", "What belief are you here to change?", ["Find the belief-shifting WTF idea worth becoming known for."]],
  ["02", "Blueprint", "What transformation can you own?", ["Turn your idea into your unique method, intellectual property, book and offer ecosystem."]],
  ["03", "Embody", "Are you actually living the message?", ["Stop hiding behind polished content.", "Become the evidence.", "Fully express what you actually believe."]],
  ["04", "Systemize", "How does your wisdom become a business?", ["Package the transformation into a high-value offer and build the AI-powered Freedom Funnel that captures, follows up with and moves the right people toward buying."]],
  ["05", "Transmit", "How do you become known for ONE thing?", ["Your book. Your stories. Your content. Your conversations. Your community.", "Different formats. Same belief."]],
];

const draw = { initial: { pathLength: 0 }, whileInView: { pathLength: 1 }, viewport: { once: true, amount: 0.15 }, transition: { duration: 2.2, ease: "easeInOut" } };

const Thread = () => (
  <svg className="thread-h" viewBox="0 0 1000 60" preserveAspectRatio="none" aria-hidden="true">
    <motion.path {...draw} d="M60 32C150 8 190 54 260 30S420 8 500 31 650 54 740 29 880 10 940 32" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
  </svg>
);

const Step = ([n, name, q, a]) => {
  const Icon = SKETCH[name];
  return (
    <article key={n} className="dd-step" data-testid={`dbest-step-${n}`}>
      <div className="dd-badge"><Icon /></div>
      <div>
        <p className="dstep-n">{n} — {name}</p>
        <h3 className="dd-q">{q}</h3>
        <Lines items={a} />
      </div>
    </article>
  );
};

export const DbestDrawn = () => (
  <section className="sec dd" data-testid="dbest-section">
    <InkFilter />
    <Kicker>How we build it</Kicker>
    <Big>The DBEST Method™</Big>
    <div className="dd-steps">
      <Thread />
      {STEPS.map(Step)}
    </div>
    <p className="dd-end" data-testid="dbest-result"><ArrowRight size={18} /> Consistent cash while being fully expressed.</p>
  </section>
);

const CASES = [
  { id: "1", title: "Book → sold-out high-ticket offer", img: "/img/case1.webp", alt: "Client with her book Healing Is Real and her original testimonial",
    quote: "“Happy Tuesday!! I’m still in shock. We channeled my 18-chapter book in just 45 minutes and I sold out my high ticket offer in 3 weeks!”",
    rows: [["Book", "18-chapter book created"], ["Offer", "High-ticket transformation"], ["Result", "Sold out in 3 weeks"]], lit: ["Transmit", "Systemize"] },
  { id: "2", title: "Book → offer → automated backend", img: "/img/case2.webp", alt: "Client with his Be A Hero books and his original testimonial",
    quote: "“We finished my book in 2 weeks, set up a landing page with my high-ticket offer, and had a CRM system completely set up to collect & nurture leads automatically!!!”",
    rows: [["Book", "Completed in 2 weeks"], ["Offer", "High-ticket offer positioned"], ["System", "Landing page + CRM + automated lead nurture"]], lit: ["Transmit", "Systemize"] },
  { id: "3", title: "Wisdom → unique method → high-ticket sales", img: "/img/case3.webp", alt: "Client with her book and her original testimonial",
    quote: "“Fastest process EVER!! With Human Design and Gene Keys we created a custom framework that’s helping me build momentum and SELL high-ticket offers.”",
    rows: [["Blueprint", "Custom framework"], ["Authority", "Unique intellectual property"], ["Offer", "High-ticket sales"]], lit: ["Blueprint", "Systemize"] },
  { id: "4", title: "Idea → book → high-ticket ascension", img: "/img/tm-freedom.webp", alt: "Client book cover: Finding Your Freedom Frequency", contain: true,
    quote: "“Just finished my book in 1 day @ ascension plan for high-ticket offer after a few sessions with Dasha. My landing page is set up and I am ready to sell my offer 1→many.”",
    rows: [["Book", "Finished in 1 day"], ["Offer", "High-ticket ascension plan"], ["System", "Landing page ready to sell one-to-many"]], lit: ["Transmit", "Systemize"] },
];

const Letters = ({ lit, id }) => (
  <div className="sp-letters" aria-label={`DBEST steps used: ${lit.join(", ")}`} data-testid={`case-study-${id}-dbest`}>
    {STEPS.map(([, name]) => <span key={name} className={lit.includes(name) ? "on" : ""} title={name}>{name[0]}</span>)}
  </div>
);

const Case = ({ id, title, img, alt, quote, rows, contain, lit }) => (
  <article className="story case" data-testid={`case-study-${id}`}>
    <p className="story-label">Case study {id}</p>
    <h3 className="case-title">{title}</h3>
    <Letters lit={lit} id={id} />
    <img src={img} alt={alt} loading="lazy" className={`story-img${contain ? " contain" : ""}`} />
    <blockquote className="story-quote">{quote}</blockquote>
    <dl className="case-rows">
      {rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
    </dl>
    <Disclaimer />
  </article>
);

const Messages = () => (
  <div className="proof fold" data-testid="client-messages">
    <div className="quotes">
      {TESTIMONIALS.map((t, i) => (
        <figure key={i} className={`quote${t.img ? " has-img" : ""}`} data-testid={`testimonial-${i}`}>
          {t.img && <img src={`/img/${t.img}.webp`} alt="Client with her published book" loading="lazy" className="quote-img" />}
          <blockquote>{t.q}</blockquote>
          {t.by && <figcaption>— {t.by}</figcaption>}
        </figure>
      ))}
    </div>
    <p className="disclaimer light-d center-t">Individual client experiences. Results vary.</p>
  </div>
);

export const CaseStudies = () => {
  const [open, setOpen] = useState(false);
  return (
    <section className="sec" data-testid="client-proof-section">
      <Big className="center-t">Don’t take my word for it.</Big>
      <p className="sub-h center-t">Look at what they actually built.</p>
      <div className="stories">{CASES.map((c) => <Case key={c.id} {...c} />)}</div>
      <button type="button" className="btn-ghost dark-ghost" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="client-messages-toggle">
        {open ? "Hide client messages" : `Read all ${TESTIMONIALS.length} client messages`} <ChevronDown size={16} className={open ? "rot" : ""} />
      </button>
      {open && <Messages />}
    </section>
  );
};
